import { z } from "zod";

/**
 * Validation Schemas for Input Security
 * 
 * All user inputs must be validated to prevent:
 * - Injection attacks
 * - Data corruption
 * - Malformed data in database
 */

// Email validation
export const emailSchema = z
  .string()
  .trim()
  .email({ message: "Please enter a valid email address" })
  .max(255, { message: "Email must be less than 255 characters" })
  .toLowerCase();

// Password validation
export const passwordSchema = z
  .string()
  .min(6, { message: "Password must be at least 6 characters long" })
  .max(128, { message: "Password must be less than 128 characters" })
  .regex(/^(?=.*[a-zA-Z])/, {
    message: "Password must contain at least one letter",
  });

// Display name validation
export const displayNameSchema = z
  .string()
  .trim()
  .min(1, { message: "Name cannot be empty" })
  .max(50, { message: "Name must be less than 50 characters" })
  .regex(/^[a-zA-Z0-9\s._-]+$/, {
    message: "Name can only contain letters, numbers, spaces, dots, underscores, and hyphens",
  });

// Auth form validation
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, { message: "Password is required" }),
});

export const signupSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
});

export const resetPasswordSchema = z.object({
  email: emailSchema,
});

// Profile validation
export const profileSchema = z.object({
  displayName: displayNameSchema,
  journeyStage: z.string().min(1, { message: "Please select your journey stage" }),
  symptoms: z.array(z.string()).optional(),
  goals: z.array(z.string()).optional(),
  avatarUrl: z.string().url().nullable().optional(),
});

// Community post validation
export const postSchema = z.object({
  title: z
    .string()
    .trim()
    .min(3, { message: "Title must be at least 3 characters" })
    .max(200, { message: "Title must be less than 200 characters" }),
  content: z
    .string()
    .trim()
    .min(10, { message: "Content must be at least 10 characters" })
    .max(10000, { message: "Content must be less than 10,000 characters" }),
  category: z.string().min(1, { message: "Please select a category" }),
});

// Comment validation
export const commentSchema = z.object({
  content: z
    .string()
    .trim()
    .min(1, { message: "Comment cannot be empty" })
    .max(5000, { message: "Comment must be less than 5,000 characters" }),
});
